import React from "react";
import {
  Database,
  Mic,
  Sprout,
  Fingerprint,
  CalendarRange,
  Trees,
  ClipboardList,
  Archive,
  MapIcon,
} from "lucide-react";
import { SECTIONS } from "@/constants/testIds";

const NODES = [
  { icon: Database, label: "Species\nDatabases" },
  { icon: Mic, label: "Language\nRecordings" },
  { icon: Sprout, label: "Seed\nCollection" },
  { icon: Fingerprint, label: "Provenance\nData" },
  { icon: CalendarRange, label: "Seasonal\nCalendars" },
  { icon: Trees, label: "Restoration\nPlanning" },
  { icon: ClipboardList, label: "Ranger\nMonitoring" },
  { icon: Archive, label: "Cultural\nArchives" },
];

export default function KnowledgeSystems() {
  return (
    <section
      id="knowledge"
      data-testid={SECTIONS.knowledge}
      className="relative py-24 md:py-32 bg-[#FDFBF7]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mb-16">
          <div className="bm-eyebrow mb-6">Section 10 — Evolution</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#2D2621]">
            From Maps to Knowledge Systems
          </h2>
          <p className="mt-6 text-[#584D45] text-lg leading-relaxed">
            Bush Maps are often the first step towards larger,
            community-owned knowledge systems — living archives that grow,
            adapt and stay in community hands.
          </p>
        </div>

        {/* Ecosystem diagram */}
        <div className="relative bg-[#F3EFEA] border border-[#E2DACF] p-8 md:p-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 items-stretch">
            {NODES.slice(0, 4).map(({ icon: Icon, label }, i) => (
              <NodeCard key={i} Icon={Icon} label={label} />
            ))}
          </div>

          {/* Central hub */}
          <div className="my-10 md:my-14 flex flex-col items-center">
            <div className="hidden md:block w-px h-10 bg-[#C5B6A3]" />
            <div className="relative w-44 h-44 md:w-52 md:h-52 rounded-full bg-[#2C3C2B] text-[#E9C7A8] flex flex-col items-center justify-center text-center shadow-lg ring-8 ring-[#FDFBF7]">
              <MapIcon size={32} />
              <div className="mt-3 font-serif-display text-xl leading-tight px-6">
                Community
                <br /> Knowledge Hub
              </div>
            </div>
            <div className="hidden md:block w-px h-10 bg-[#C5B6A3]" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 items-stretch">
            {NODES.slice(4, 8).map(({ icon: Icon, label }, i) => (
              <NodeCard key={i} Icon={Icon} label={label} />
            ))}
          </div>
        </div>

        <p className="mt-10 text-[#7A6C63] text-sm tracking-wide max-w-2xl">
          Every layer is opt-in. Communities decide what becomes part of the
          system, who can access it, and how it evolves.
        </p>
      </div>
    </section>
  );
}

function NodeCard({ Icon, label }) {
  return (
    <div className="bg-[#FDFBF7] border border-[#E2DACF] p-5 md:p-6 flex flex-col items-center text-center hover:border-[#A95A3F] transition-colors group">
      <span className="w-12 h-12 rounded-full bg-[#EBE4D8] flex items-center justify-center text-[#6B7A6A] group-hover:bg-[#A95A3F] group-hover:text-white transition-colors">
        <Icon size={20} />
      </span>
      <div className="mt-3 font-serif-display text-lg text-[#2D2621] leading-tight whitespace-pre-line">
        {label}
      </div>
    </div>
  );
}
